/**
 * The `.ics` an "Add to calendar" tap hands the phone — Phase 9, D10 (i).
 *
 * Pure and dependency-free on purpose, the same reason `filmRank.ts` is (see
 * `entries.ts`'s note above its re-export): the Worker's offline test suite
 * loads it directly, with no build step and no extensionless import Node's
 * loader cannot follow. `FeedRow` is a type-only import — erased before
 * either runtime ever sees it — so it costs nothing here.
 */
import type { FeedRow } from './types'
import { sheetPlace, type PlaceShowing, type PlaceVenue } from './sheetPlace.ts'
import { FALLBACK_ZONE, clockIn, dayIn, midnightIsoIn, zoneOfCity } from './zone.ts'

export interface IcsEvent {
  uid: string
  /** 'YYYY-MM-DDTHH:MM' wall-clock in `tz`, or 'YYYY-MM-DD' when `allDay`. */
  start: string
  /** The row's zone (C2.1: its town's region's), named in `DTSTART;TZID=`. */
  tz: string
  allDay: boolean
  durationMin: number
  title: string
  location: string | null
  url: string | null
  description: string | null
}

/** Minutes, by type, for a row with no measured length of its own. An
 *  exhibition is never timed by this table — it is always `allDay` — so it
 *  carries no entry. */
const DEFAULT_DURATION: Record<string, number> = {
  concert: 105,
  performance: 135,
  screening: 110,
  opening: 180,
  talk: 90,
  tour: 120,
  workshop: 180,
  market: 240,
}

// The calendar day of a timestamp is `zone.ts`'s `dayIn(at, tz)`, and the
// midnight placeholder for a bare day is `midnightIsoIn(day, tz)` (C2.1): a
// caller choosing which showing to pass in (the sheet's own date picker), WP
// 3.3's `scheduleEntry` deriving `commitments.on_date` from a showing's real
// `starts_at`, and the undated-run picker all name the zone they mean — the
// row's, `zoneOfCity(row.city)`. A fixed offset gets the day wrong across a
// clock change: `2026-12-14T00:00:00+02:00` is the 13th in Berlin's winter.

/** Wall-clock HH:MM in `tz`, or null for the midnight a dateless extraction
 *  wrote — the same rule `localTime` in entries.ts applies to a card; a
 *  showing with no real time is no better than no showing at all. For the
 *  Year's day pane, its items and Today's commitments, which printed
 *  `starts_at.slice(11, 16)` — the UTC clock, an hour or two early (a3-02). */
export function timeIn(at: string, tz: string): string | null {
  const t = clockIn(at, tz)
  return t === '00:00' || t === '24:00' ? null : t
}

// Berlin by name, kept for `test/ics.test.ts`, which pins the Berlin rules
// these three were written for. Nothing in the app calls them; it names a
// zone (C2.1).

/** The Berlin calendar day a timestamp falls on: `dayIn(at, FALLBACK_ZONE)`. */
export function berlinDate(at: string): string {
  return dayIn(at, FALLBACK_ZONE)
}

/** Midnight in Berlin with that day's real offset: `midnightIsoIn`. */
export function berlinMidnightIso(date: string): string {
  return midnightIsoIn(date, FALLBACK_ZONE)
}

/** Berlin wall-clock HH:MM or null: `timeIn(at, FALLBACK_ZONE)`. */
export function berlinTime(at: string): string | null {
  return timeIn(at, FALLBACK_ZONE)
}

/**
 * The last part of the location line: the postcode and the *town*, which was
 * the literal "Berlin" (C2.3). A Potsdam venue read "14467 Berlin" and a
 * Concord one would have read the same. The town is the row's own `city`; a
 * row with none is a Berlin row (survey trap T2) only in Berlin's zone, and
 * nowhere else is a town invented.
 *
 * The order is the zone's country's: German addresses say "10785 Berlin",
 * American ones "Concord 01742" (no state on the row, so no "MA"). With no
 * postcode a Berlin row prints nothing, as it always did; elsewhere the town
 * alone, since "{street}, {town}" is the whole of what a calendar needs.
 */
function townLine(postcode: string | null, city: string | null | undefined, tz: string): string | null {
  const town = city?.trim() || (tz === FALLBACK_ZONE ? 'Berlin' : null)
  const postcodeFirst = (ZONES.get(tz) ?? ZONES.get(FALLBACK_ZONE)!).postcodeFirst
  if (postcode) return town ? (postcodeFirst ? `${postcode} ${town}` : `${town} ${postcode}`) : postcode
  return tz === FALLBACK_ZONE ? null : town
}

function formatDuration(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h && m) return `${h} h ${m} min`
  if (h) return `${h} h`
  return `${m} min`
}

/**
 * The `.ics` event for one day of an entry's run.
 *
 * `showing` is the one the overflow menu's `.ics` download resolved for
 * `date` — already narrowed to this entry — or null where the picked day has
 * none. Rule 2: the chosen showing's time when it is on `date` and has one;
 * a day with no showing, or a showing whose source printed no usable time,
 * is all-day. An exhibition is always all-day, whatever `showing` carries —
 * the run has an opening hour, which is a different decision from the visit
 * itself, and is not what "Add to calendar" is scheduling.
 */
export function icsFor(
  e: FeedRow,
  showing: ({ starts_at: string } & PlaceShowing) | null,
  date: string,
  /** WP 2.4: `summary` is `feed_detail`'s now that the card that printed it is
   *  gone, so it is no longer on `e` and has to be handed in. Optional, and the
   *  description simply loses a paragraph without it — the alternative was
   *  leaving `e.summary` here reading a column the view no longer returns,
   *  which is the "source left behind by the thing it sourced" bug CLAUDE.md
   *  lists, and it would have been silent. */
  summary?: string | null,
  /** The venues the app holds, by id (`useVenues()`), for a showing at a
   *  venue other than the row's own — UI-audit a1-01. Without it such a
   *  showing's location is its venue's name alone, never the row's street. */
  venueOf?: (id: string) => PlaceVenue | null | undefined,
): IcsEvent {
  const isExhibition = e.type === 'exhibition'
  // The row's own zone (C2.1): the day it was handed and the time it prints
  // are the venue's, which is what the phone's calendar is told.
  const tz = zoneOfCity(e.city)
  const onDate = showing && dayIn(showing.starts_at, tz) === date ? showing : null
  const time = !isExhibition && onDate ? timeIn(onDate.starts_at, tz) : null
  const allDay = isExhibition || !time

  const durationMin = e.duration_min ?? e.film?.runtime_min ?? DEFAULT_DURATION[e.type] ?? 120
  // Rule 3: the description says which kind of number this is — a fact
  // about this showing, or this type's usual evening.
  const measured = e.duration_min != null || typeof e.film?.runtime_min === 'number'

  // UI-audit a1-01: the place is the showing's, whole — its name, its
  // street, its booking link — and never the showing's name on the row's
  // street, which is what a film at eleven cinemas used to get.
  const place = sheetPlace(e, onDate, venueOf)
  const location = [place.venue, place.street, townLine(place.postcode, e.city, tz)]
    .filter(Boolean).join(', ') || null

  const url = place.page
  const durationNote = allDay ? null : measured
    ? `About ${formatDuration(durationMin)}.`
    : `Typical length: ${formatDuration(durationMin)}.`
  // Rule: the booking link only when it says something the calendar's own
  // URL field does not — the same "only when it differs" rule the sheet
  // already uses for a newsletter organiser.
  const description = [summary, durationNote, place.booking && place.booking !== e.url ? place.booking : null]
    .filter(Boolean).join('\n\n') || null

  return {
    uid: `${e.id}-${date}@schnauzelist.com`,
    tz,
    start: allDay ? date : `${date}T${time}`,
    allDay,
    durationMin,
    title: e.title,
    location,
    url,
    description,
  }
}

/** Backslash first, then the characters it would otherwise double-escape —
 *  RFC 5545 §3.3.11. A literal newline in a museum's own text becomes `\n`,
 *  not a second physical line the folding below would then have to guess
 *  the meaning of. */
function escapeText(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

/** RFC 5545 §3.1: no physical line over 75 octets, continued with a CRLF and
 *  a single leading space. Budgeted in UTF-8 octets, not characters — a
 *  German venue name spends more than one octet per accent. */
function foldLine(line: string): string {
  const enc = new TextEncoder()
  if (enc.encode(line).length <= 75) return line

  const segments: string[] = []
  let current = ''
  let bytes = 0
  for (const ch of line) {
    const chBytes = enc.encode(ch).length
    // The first segment gets the full 75 octets; every continuation loses
    // one to the leading space that marks it as one.
    const budget = segments.length === 0 ? 75 : 74
    if (bytes + chBytes > budget) {
      segments.push(current)
      current = ''
      bytes = 0
    }
    current += ch
    bytes += chBytes
  }
  if (current) segments.push(current)
  return segments.map((seg, i) => (i === 0 ? seg : ` ${seg}`)).join('\r\n')
}

/**
 * What a calendar file needs to know about a zone, from a small fixed table
 * for the two zones the app reads in — not computed (C2.3). A `TZID` with no
 * `VTIMEZONE` worked for Berlin only because every calendar app knows the
 * name; RFC 5545 §3.6.5 wants the block, and a client that does not know the
 * name (or that is told a different rule) reads the time as floating. Each
 * block carries the zone's current rules (the EU's last Sundays of March and
 * October; the US's second Sunday of March and first of November, since
 * 2007), which is the right answer for every date the app can show.
 *
 * `postcodeFirst` is the order an address is written in ("10785 Berlin",
 * "Concord 01742"), kept here because this is where a zone is described.
 */
const ZONES = new Map<string, { postcodeFirst: boolean; vtimezone: string[] }>([
  ['Europe/Berlin', {
    postcodeFirst: true,
    vtimezone: [
      'BEGIN:VTIMEZONE',
      'TZID:Europe/Berlin',
      'BEGIN:STANDARD',
      'DTSTART:19701025T030000',
      'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
      'TZOFFSETFROM:+0200',
      'TZOFFSETTO:+0100',
      'TZNAME:CET',
      'END:STANDARD',
      'BEGIN:DAYLIGHT',
      'DTSTART:19700329T020000',
      'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
      'TZOFFSETFROM:+0100',
      'TZOFFSETTO:+0200',
      'TZNAME:CEST',
      'END:DAYLIGHT',
      'END:VTIMEZONE',
    ],
  }],
  ['America/New_York', {
    postcodeFirst: false,
    vtimezone: [
      'BEGIN:VTIMEZONE',
      'TZID:America/New_York',
      'BEGIN:STANDARD',
      'DTSTART:19701101T020000',
      'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
      'TZOFFSETFROM:-0400',
      'TZOFFSETTO:-0500',
      'TZNAME:EST',
      'END:STANDARD',
      'BEGIN:DAYLIGHT',
      'DTSTART:19700308T020000',
      'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
      'TZOFFSETFROM:-0500',
      'TZOFFSETTO:-0400',
      'TZNAME:EDT',
      'END:DAYLIGHT',
      'END:VTIMEZONE',
    ],
  }],
])

const stamp = () => new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'

/** The `VEVENT` lines of one event. A timed one names its zone in `DTSTART`;
 *  an all-day one names none, so it asks for no `VTIMEZONE` either. */
function veventLines(ev: IcsEvent): string[] {
  const [datePart, timePart] = ev.start.split('T')
  const compactDate = datePart.replace(/-/g, '')
  const dtstart = ev.allDay
    ? `DTSTART;VALUE=DATE:${compactDate}`
    : `DTSTART;TZID=${ev.tz}:${compactDate}T${timePart!.replace(':', '')}00`
  // An all-day event's duration is the one day it occupies, not the minutes
  // `durationMin` still carries for the description above.
  const duration = ev.allDay ? 'P1D' : `PT${ev.durationMin}M`

  return [
    'BEGIN:VEVENT',
    `UID:${escapeText(ev.uid)}`,
    `DTSTAMP:${stamp()}`,
    dtstart,
    `DURATION:${duration}`,
    `SUMMARY:${escapeText(ev.title)}`,
    ev.location && `LOCATION:${escapeText(ev.location)}`,
    ev.url && `URL:${ev.url}`,
    ev.description && `DESCRIPTION:${escapeText(ev.description)}`,
    'END:VEVENT',
  ].filter((x): x is string => Boolean(x))
}

/**
 * VCALENDAR text for these events, CRLF throughout, folded to 75 octets — the
 * shape `<a download>` hands the phone. One `VTIMEZONE` for each zone a timed
 * event actually names, before the first `VEVENT` (RFC 5545 §3.6.5), in the
 * order the zones first appear; a zone the table does not hold gets none, and
 * its `TZID` is left for the client to resolve by name as it always was.
 */
export function renderIcsCalendar(events: readonly IcsEvent[]): string {
  const used: string[] = []
  for (const ev of events) if (!ev.allDay && !used.includes(ev.tz) && ZONES.has(ev.tz)) used.push(ev.tz)

  const raw = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//schnauzelist//EN',
    ...used.flatMap(tz => ZONES.get(tz)!.vtimezone),
    ...events.flatMap(veventLines),
    'END:VCALENDAR',
  ]

  return raw.map(foldLine).join('\r\n') + '\r\n'
}

/** VCALENDAR/VEVENT text for one event: `renderIcsCalendar` of one. */
export function renderIcs(ev: IcsEvent): string {
  return renderIcsCalendar([ev])
}
